import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import apiService, {
  ResumeItem,
  UploadResumeResponse,
  PresignRequest,
} from "../services/apiService";
import { useSubscriptionContext } from "../contexts/SubscriptionContext";
import { useAuth } from "../contexts/AuthContext";

interface UseResumesState {
  resumes: ResumeItem[];
  isLoading: boolean;
  isUploading: boolean;
  isDeleting: boolean;
  deletingResumeId: string | null;
  activeUploadError: string | null;
}

const POLL_INTERVAL_MS = 15000; // 15 seconds
const INITIAL_POLL_DELAY_MS = 25000; // 25 seconds for first poll
const MAX_POLL_DURATION_MS = 15 * 60 * 1000; // 15 minutes — AI rewrite closure loop can take up to ~12 min

const normalizeResume = (resume: ResumeItem): ResumeItem => {
  // Handle null/undefined resume
  if (!resume) {
    return {
      id: "unknown",
      originalFilename: "Unknown",
      atsProcessingStatus: "unknown",
    } as ResumeItem;
  }

  return {
    ...resume,
    // Normalize ID field - backend may return 'id', 'resume_id', or 'resumeId'
    id:
      resume.id ||
      (resume as any).resume_id ||
      (resume as any).resumeId ||
      "unknown",
    // Map API response fields to expected fields
    originalFilename:
      resume.filename || resume.originalFilename || resume.name || "Unknown",
    fileSize: resume.file_size || resume.fileSize,
    fileType: resume.file_type || resume.fileType,
    storagePath: resume.s3_key || resume.storagePath || resume.fileName,
    uploadedAt: resume.created_at || resume.uploadedAt || resume.uploadDate,
    // Normalize backend statuses to UI statuses
    atsProcessingStatus: (() => {
      const rawStatus =
        resume.atsProcessingStatus || resume.status || "pending";
      // Map backend status values to frontend status values
      if (rawStatus === "in_progress") return "processing";
      if (rawStatus === "completed") return "processed";
      if (rawStatus === "processed") return "completed";
      return rawStatus;
    })(),
    atsScore: resume.atsScore,
    // Derive compliance % from atsData if present (backend provides 0-1 float)
    atsComplianceScorePct: (() => {
      const data: any = (resume as any).atsData;
      const raw =
        data?.atsComplianceScore ?? (resume as any).atsComplianceScore;
      if (raw == null) return null;
      const val = typeof raw === "number" ? raw : parseFloat(String(raw));
      if (Number.isNaN(val)) return null;
      const normalized = val > 1 ? val / 100 : val; // handle 0-100 or 0-1
      return Math.max(0, Math.min(100, Math.round(normalized * 100)));
    })(),
    lastProcessedAt: resume.processed_at || resume.lastProcessedAt,
  };
};

const computeSHA256Checksum = async (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const arrayBuffer = reader.result as ArrayBuffer;
        const hashBuffer = await crypto.subtle.digest("SHA-256", arrayBuffer);
        const hashArray = new Uint8Array(hashBuffer);
        const hashBase64 =
          typeof Buffer !== "undefined"
            ? Buffer.from(hashArray).toString("base64")
            : btoa(
                Array.from(hashArray, (byte) => String.fromCharCode(byte)).join(
                  "",
                ),
              );
        resolve(hashBase64);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
};

export const useResumes = () => {
  const { reloadSubscription } = useSubscriptionContext();
  const { refreshProfile } = useAuth();
  const [state, setState] = useState<UseResumesState>({
    resumes: [],
    isLoading: false,
    isUploading: false,
    isDeleting: false,
    deletingResumeId: null,
    activeUploadError: null,
  });
  const pollers = useRef<Record<string, number>>({});
  const profileRefreshTimers = useRef<number[]>([]);

  const updateResumes = useCallback(
    (updater: (items: ResumeItem[]) => ResumeItem[]) => {
      setState((prev) => ({
        ...prev,
        resumes: updater(prev.resumes),
      }));
    },
    [],
  );

  const clearPoller = useCallback((resumeId: string) => {
    const handle = pollers.current?.[resumeId];
    if (handle) {
      window.clearTimeout(handle);
      delete pollers.current[resumeId];
    }
  }, []);

  const clearProfileRefreshTimers = useCallback(() => {
    profileRefreshTimers.current.forEach((handle) =>
      window.clearTimeout(handle),
    );
    profileRefreshTimers.current = [];
  }, []);

  const scheduleProfileRefresh = useCallback(
    (delayMs: number = 0) => {
      const timerId = window.setTimeout(() => {
        profileRefreshTimers.current = profileRefreshTimers.current.filter(
          (handle) => handle !== timerId,
        );
        refreshProfile().catch((error) => {
          console.error(
            "Failed to refresh profile after resume update:",
            error,
          );
        });
      }, delayMs);

      profileRefreshTimers.current.push(timerId);
    },
    [refreshProfile],
  );

  const fetchResumes = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true, activeUploadError: null }));
    try {
      const response = await apiService.getResumes();
      // CRITICAL: Handle both array and object responses
      // Backend might return { resumes: [...] } or just [...]
      let items: ResumeItem[] = [];
      if (Array.isArray(response)) {
        items = response;
      } else if (response && typeof response === "object") {
        // Check for common property names
        if (Array.isArray(response.resumes)) {
          items = response.resumes;
        } else if (Array.isArray(response.data)) {
          items = response.data;
        } else if (Array.isArray(response.items)) {
          items = response.items;
        } else {
          // If it's an object but not an array, log and default to empty
          console.warn("getResumes returned non-array response:", response);
          items = [];
        }
      }
      setState((prev) => ({
        ...prev,
        resumes: items.map(normalizeResume),
        isLoading: false,
      }));
    } catch (error) {
      setState((prev) => ({ ...prev, isLoading: false }));
      toast.error("Unable to load resumes right now");
    }
  }, []);

  const scheduleStatusPoll = useCallback(
    (resumeId: string, isInitialPoll: boolean = false) => {
      // Guard against undefined or invalid resume IDs
      if (!resumeId || resumeId === "undefined" || resumeId === "unknown") {
        console.error(
          "Cannot schedule status poll: invalid resume ID",
          resumeId,
        );
        return;
      }

      clearPoller(resumeId);

      // Calculate delay: 25 seconds for first poll, 15 seconds for subsequent polls
      const delay = isInitialPoll ? INITIAL_POLL_DELAY_MS : POLL_INTERVAL_MS;

      // Track when polling started for this resume
      const pollStartTime = Date.now();

      const handle = window.setTimeout(async () => {
        try {
          // Check if we've exceeded the maximum polling duration (5 minutes)
          if (Date.now() - pollStartTime > MAX_POLL_DURATION_MS) {
            updateResumes((items) =>
              items.map((item) =>
                item.id === resumeId
                  ? normalizeResume({
                      ...item,
                      atsProcessingStatus: "timeout",
                    })
                  : item,
              ),
            );
            toast.error(
              "ATS processing is taking longer than expected. Please try uploading again.",
            );
            return;
          }

          const status = await apiService.getResumeStatus(resumeId);

          // Use atsProcessingStatus if available, fallback to status
          const atsStatus = status.atsProcessingStatus || status.status;

          updateResumes((items) =>
            items.map((item) =>
              item.id === resumeId
                ? normalizeResume({
                    ...item,
                    status: status.status ?? item.status,
                    atsProcessingStatus: atsStatus,
                    atsScore: status.atsScore ?? item.atsScore,
                    fileSize: status.fileSize ?? item.fileSize,
                  })
                : item,
            ),
          );

          // Handle different status scenarios based on ATS processing status
          if (atsStatus === "processing" || atsStatus === "in_progress") {
            scheduleStatusPoll(resumeId, false); // Continue polling
          } else if (atsStatus === "uploaded" || atsStatus === "pending") {
            // Resume is uploaded but ATS processing hasn't started yet
            scheduleStatusPoll(resumeId, false); // Continue polling
          } else if (atsStatus === "processed" || atsStatus === "completed") {
            toast.success(
              `ATS processing completed! Score: ${status.atsScore || "N/A"}%`,
            );
            // Reload subscription to update token balance after ATS processing completes
            reloadSubscription().catch((error) => {
              console.error(
                "Failed to reload subscription after ATS processing:",
                error,
              );
            });
            scheduleProfileRefresh();
            // Stop polling when processing is complete
          } else if (atsStatus === "error" || atsStatus === "failed") {
            toast.error("ATS processing failed. Try uploading again.");
            // Stop polling on error
          } else {
            // For any other status, continue polling
            scheduleStatusPoll(resumeId, false);
          }
        } catch (error) {
          toast.error("Unable to check resume status");
          // Continue polling even on error, but with longer interval
          setTimeout(
            () => scheduleStatusPoll(resumeId, false),
            POLL_INTERVAL_MS * 2,
          );
        }
      }, delay);
      pollers.current[resumeId] = handle;
    },
    [updateResumes, reloadSubscription, scheduleProfileRefresh],
  );

  const uploadResume = useCallback(
    async (file: File, resumeType: string) => {
      setState((prev) => ({
        ...prev,
        isUploading: true,
        activeUploadError: null,
      }));
      try {
        // Step 1: Compute SHA-256 checksum
        const checksumSHA256 = await computeSHA256Checksum(file);

        // Step 2: Get presigned URL
        const presignRequest: PresignRequest = {
          filename: file.name,
          contentType: file.type,
          fileSize: file.size,
          checksumSHA256,
        };

        const presignResponse =
          await apiService.getPresignedUrl(presignRequest);

        // Step 3: Upload to S3 using presigned URL
        let uploadResponse;
        try {
          uploadResponse = await fetch(presignResponse.presignedUrl, {
            method: "PUT",
            body: file,
            headers: {
              "Content-Type": file.type,
              "x-amz-checksum-sha256": checksumSHA256,
            },
            // Add mode to ensure CORS is handled correctly
            mode: "cors",
            credentials: "omit", // Don't send cookies to S3
          });
        } catch (fetchError: any) {
          throw fetchError;
        }

        if (!uploadResponse.ok) {
          throw new Error(
            `S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`,
          );
        }

        // Step 4: Create resume record in DynamoDB
        const createResponse = await apiService.createResume({
          s3Key: presignResponse.s3Key,
          filename: file.name,
          fileSize: file.size,
          fileType: file.type,
          resumeType: resumeType,
        });

        // Extract resume data from response (backend returns {success: true, data: {...}})
        const resumeData = createResponse?.data || createResponse;

        // Normalize resume data to ensure ID is properly set
        const normalizedResume = normalizeResume(resumeData);

        // Validate that we have a valid resume ID before proceeding
        if (!normalizedResume.id || normalizedResume.id === "unknown") {
          console.error("Resume created but ID is missing or invalid:", {
            createResponse,
            resumeData,
            normalizedResume,
          });
          throw new Error(
            "Resume was created but ID is missing. Please refresh and try again.",
          );
        }

        // Update local state with the created resume
        updateResumes((items) => [normalizedResume, ...items]);
        toast.success(
          "Resume uploaded successfully! ATS processing will begin shortly.",
        );

        // Change status to "ATS Processing" after 5 seconds
        setTimeout(() => {
          updateResumes((items) =>
            items.map((item) =>
              item.id === normalizedResume.id
                ? normalizeResume({
                    ...item,
                    atsProcessingStatus: "processing",
                  })
                : item,
            ),
          );
        }, 5000); // 5 seconds

        // Only start polling if we have a valid resume ID
        scheduleStatusPoll(normalizedResume.id, true); // Start with initial 25-second delay

        // Return a response that matches the expected format
        const response: UploadResumeResponse = {
          success: true,
          data: resumeData,
          message: "Resume uploaded successfully",
        };

        return response;
      } catch (error) {
        const message = (error as Error).message || "Upload failed";
        setState((prev) => ({ ...prev, activeUploadError: message }));
        toast.error(message);
        throw error;
      } finally {
        setState((prev) => ({ ...prev, isUploading: false }));
      }
    },
    [scheduleStatusPoll, updateResumes],
  );

  const processResume = useCallback(
    async (resumeId: string) => {
      try {
        await apiService.processResume(resumeId);
        updateResumes((items) =>
          items.map((item) =>
            item.id === resumeId
              ? normalizeResume({
                  ...item,
                  atsProcessingStatus: "processing",
                })
              : item,
          ),
        );
        scheduleStatusPoll(resumeId, true); // Start with initial 35-second delay
        toast.success("ATS processing started");
      } catch (error) {
        toast.error("Unable to start ATS processing");
      }
    },
    [scheduleStatusPoll, updateResumes],
  );

  const deleteResume = useCallback(
    async (resumeId: string) => {
      try {
        setState((prev) => ({
          ...prev,
          isDeleting: true,
          deletingResumeId: resumeId,
        }));
        await apiService.deleteResume(resumeId);
        clearPoller(resumeId);
        updateResumes((items) => items.filter((item) => item.id !== resumeId));
        toast.success("Resume deleted");
        scheduleProfileRefresh(1500);
      } catch (error) {
        toast.error("Unable to delete resume");
      } finally {
        setState((prev) => ({
          ...prev,
          isDeleting: false,
          deletingResumeId: null,
        }));
      }
    },
    [clearPoller, scheduleProfileRefresh, updateResumes],
  );

  useEffect(() => {
    fetchResumes();
    return () => {
      Object.keys(pollers.current).forEach(clearPoller);
      clearProfileRefreshTimers();
    };
  }, [clearPoller, clearProfileRefreshTimers, fetchResumes]);

  const clearUploadError = useCallback(() => {
    setState((prev) => ({ ...prev, activeUploadError: null }));
  }, []);

  return {
    resumes: state.resumes,
    isLoading: state.isLoading,
    isUploading: state.isUploading,
    isDeleting: state.isDeleting,
    deletingResumeId: state.deletingResumeId,
    uploadError: state.activeUploadError,
    uploadResume,
    processResume,
    deleteResume,
    refresh: fetchResumes,
    clearUploadError,
    scheduleStatusPoll, // Expose for manual status polling (e.g., after reprocessing)
    updateResumes, // Expose for updating resume state (e.g., after triggering processing)
  };
};

export type UseResumesReturn = ReturnType<typeof useResumes>;
